import apiClient from './api/client';
import ENDPOINTS from './api/endpoints';

const USE_MOCK = false;

let mockQrCodes = [
  {
    id: 1,
    tableId: 101,
    tableName: 'Table 1',
    floorName: 'Ground Floor',
    qrUrl: 'https://menu.maverickdine.com/qr/abc123',
    isActive: true,
    createdAt: '2026-01-20T09:00:00Z',
  },
  {
    id: 2,
    tableId: 102,
    tableName: 'Table 2',
    floorName: 'Ground Floor',
    qrUrl: 'https://menu.maverickdine.com/qr/def456',
    isActive: true,
    createdAt: '2026-01-20T09:05:00Z',
  },
  {
    id: 3,
    tableId: 103,
    tableName: 'Table 3',
    floorName: 'Ground Floor',
    qrUrl: 'https://menu.maverickdine.com/qr/ghi789',
    isActive: false,
    createdAt: '2026-01-20T09:10:00Z',
  },
  {
    id: 4,
    tableId: 201,
    tableName: 'Table 4',
    floorName: 'First Floor',
    qrUrl: 'https://menu.maverickdine.com/qr/jkl012',
    isActive: true,
    createdAt: '2026-01-22T11:00:00Z',
  },
  {
    id: 5,
    tableId: 202,
    tableName: 'Table 5',
    floorName: 'First Floor',
    qrUrl: 'https://menu.maverickdine.com/qr/mno345',
    isActive: true,
    createdAt: '2026-01-22T11:05:00Z',
  },
];

const mockAvailableTables = [
  { id: 301, name: 'Table 6', floorName: 'Terrace' },
  { id: 302, name: 'Table 7', floorName: 'Terrace' },
  { id: 303, name: 'Table 8', floorName: 'Terrace' },
];

let nextId = 6;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const qrCodeService = {
  async getAll(params = {}, branchId) {
    if (USE_MOCK) {
      await delay(500);
      let filtered = [...mockQrCodes];
      if (params.status === 'active') filtered = filtered.filter((q) => q.isActive);
      if (params.status === 'inactive') filtered = filtered.filter((q) => !q.isActive);
      return filtered;
    }
    const queryParams = { ...params };
    if (branchId) queryParams.branchId = branchId;
    return apiClient.get(ENDPOINTS.QR_CODES.GET_ALL, { params: queryParams });
  },

  async getById(id) {
    if (USE_MOCK) {
      await delay(300);
      return mockQrCodes.find((q) => q.id === id) || null;
    }
    return apiClient.get(ENDPOINTS.QR_CODES.GET_BY_ID(id));
  },

  async getByTable(tableId) {
    if (USE_MOCK) {
      await delay(300);
      return mockQrCodes.find((q) => q.tableId === tableId) || null;
    }
    return apiClient.get(ENDPOINTS.QR_CODES.GET_BY_TABLE(tableId));
  },

  async create(data) {
    if (USE_MOCK) {
      await delay(600);
      const table = mockAvailableTables.find((t) => t.id === data.tableId);
      const newCode = {
        id: nextId++,
        tableId: data.tableId,
        tableName: table?.name || `Table ${data.tableId}`,
        floorName: table?.floorName || 'Unknown',
        qrUrl: `https://menu.maverickdine.com/qr/${Math.random().toString(36).substring(2, 8)}`,
        isActive: true,
        createdAt: new Date().toISOString(),
      };
      mockQrCodes.push(newCode);
      // Remove from available tables
      const idx = mockAvailableTables.findIndex((t) => t.id === data.tableId);
      if (idx !== -1) mockAvailableTables.splice(idx, 1);
      return newCode;
    }
    return apiClient.post(ENDPOINTS.QR_CODES.CREATE, data);
  },

  async bulkCreate() {
    if (USE_MOCK) {
      await delay(1000);
      const newCodes = mockAvailableTables.map((table) => ({
        id: nextId++,
        tableId: table.id,
        tableName: table.name,
        floorName: table.floorName,
        qrUrl: `https://menu.mavericfdine.com/qr/${Math.random().toString(36).substring(2, 8)}`,
        isActive: true,
        createdAt: new Date().toISOString(),
      }));
      mockQrCodes.push(...newCodes);
      mockAvailableTables.length = 0;
      return newCodes;
    }
    return apiClient.post(ENDPOINTS.QR_CODES.BULK_CREATE);
  },

  async toggle(id) {
    if (USE_MOCK) {
      await delay(400);
      const code = mockQrCodes.find((q) => q.id === id);
      if (code) code.isActive = !code.isActive;
      return code;
    }
    return apiClient.put(ENDPOINTS.QR_CODES.TOGGLE(id));
  },

  async delete(id) {
    if (USE_MOCK) {
      await delay(400);
      mockQrCodes = mockQrCodes.filter((q) => q.id !== id);
      return { success: true };
    }
    return apiClient.delete(ENDPOINTS.QR_CODES.DELETE(id));
  },

  async deleteMultiple(ids) {
    if (USE_MOCK) {
      await delay(600);
      mockQrCodes = mockQrCodes.filter((q) => !ids.includes(q.id));
      return { success: true, deletedCount: ids.length };
    }
    await Promise.all(ids.map((id) => apiClient.delete(ENDPOINTS.QR_CODES.DELETE(id))));
    return { success: true, deletedCount: ids.length };
  },

  async deleteAll() {
    if (USE_MOCK) {
      await delay(600);
      mockQrCodes = [];
      return { success: true };
    }
    return apiClient.delete(ENDPOINTS.QR_CODES.DELETE_ALL);
  },

  async getAvailableTables(branchId) {
    if (USE_MOCK) {
      await delay(400);
      return [...mockAvailableTables];
    }
    const params = {};
    if (branchId) params.branchId = branchId;
    return apiClient.get(ENDPOINTS.QR_CODES.GET_ALL + '/available-tables', { params });
  },
};

export default qrCodeService;
